import {
    ButtonSecondary,
    Colors,
    ConfirmDialog,
    Container,
    FOCUS_RING,
    Icon,
    Text,
} from '@amelio/ui-library';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { AgendaEditor } from './AgendaEditor';
import { mergeGeneratedAgenda, type GeneratedAgenda } from './agendaViewModel';
import { AgendaGeneratingState } from './components/AgendaGeneratingState';
import {
    AgendaPromptModal,
    type AgendaIntegrationMode,
    type AgendaPromptModalRef,
} from './components/AgendaPromptModal';
import { SensitiveObjectiveRefusal } from './sensitiveObjectiveRefusal';
import { useAgendaEditor } from './useAgendaEditor';
import { useMeetingAgenda } from './useMeetingAgenda';

import type { AgendaItemView } from './agendaViewModel';
import type { MeetingAgenda, MeetingAgendaUpdatePayload } from '@/types/review/PostReviewMeeting';

import {
    useGetMeetingAgenda,
    useUpdateMeetingAgenda,
} from '@/api/review-services/post-review-meeting';
import { AgendaAiBanner } from '@/components/AgendaAiBanner';
import { useAmelioTranslations } from '@/hooks';
import { showErrorToast } from '@/plugins/Toast';
import { toContentLanguage, type ContentLanguage } from '@/utils/contentLanguage';

interface CycleAgendaEditorProps {
    agenda: MeetingAgenda;
    /** The reader's language, to say so when the agenda is written in the other one. */
    readerLanguage: ContentLanguage;
    disabled: boolean;
    onSave: (payload: MeetingAgendaUpdatePayload) => void;
    banner: ReactNode;
}

/** The editing half of the post-review agenda, apart so the builder can mount it afresh. */
const CycleAgendaEditor = ({
    agenda,
    readerLanguage,
    disabled,
    onSave,
    banner,
}: CycleAgendaEditorProps) => {
    const { t } = useAmelioTranslations();
    const editor = useAgendaEditor({ agenda, disabled, onSave });
    const [topicPendingDeletionId, setTopicPendingDeletionId] = useState<string | null>(null);

    // Deleting a topic takes its discussion points with it, and the employee's answers live on those
    // points -- hence the confirmation, and a message that only warns about answers when there are
    // some to lose. Read from the live topics rather than a snapshot taken when the dialog opened, so
    // an answer typed just before the click still counts.
    const wouldLoseAnswers = editor.items
        .find((item) => item.id === topicPendingDeletionId)
        ?.discussionPoints.some((point) => (point.response ?? '').trim().length > 0);

    // Same shape as the thematic meeting plan's control, which is the other host of this row.
    const renderDeleteTopic = (item: AgendaItemView) => (
        <button
            type={'button'}
            onClick={() => setTopicPendingDeletionId(item.id)}
            aria-label={t('meetingAgenda.deleteTopicNamed', { title: item.title })}
            className={`${FOCUS_RING} hover:bg-primary/10 mt-3 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full`}
            data-testid={`delete-topic-button-${item.id}`}
        >
            <Icon
                name={'essential-trash'}
                size={'sm'}
                color={Colors.BASE_500}
                ariaHidden
            />
        </button>
    );

    return (
        <>
            <AgendaEditor
                editor={editor}
                totalMeetingTimeMinutes={agenda.totalMeetingTimeMinutes}
                disabled={disabled}
                readerLanguage={readerLanguage}
                banner={banner}
                renderItemActions={disabled ? undefined : renderDeleteTopic}
                addTopicControl={
                    <ButtonSecondary
                        onClick={() => editor.topics.setIsAdding(true)}
                        disabled={disabled || !editor.topics.canAdd}
                        size={'sm'}
                        className={'mt-4'}
                        fitContent
                        shape={'pill'}
                        leftIcon={
                            <Icon
                                name={'essential-add'}
                                size={'lg'}
                                color={Colors.PRIMARY_900}
                            />
                        }
                        dataTestId={'add-topic-button'}
                    >
                        {t('meetingAgenda.addTopic')}
                    </ButtonSecondary>
                }
            />

            <ConfirmDialog
                isOpen={topicPendingDeletionId !== null}
                title={t('meetingAgenda.deleteTopicConfirm.title')}
                message={t(
                    wouldLoseAnswers
                        ? 'meetingAgenda.deleteTopicConfirm.messageWithAnswers'
                        : 'meetingAgenda.deleteTopicConfirm.message',
                )}
                confirmLabel={t('meetingAgenda.deleteTopicConfirm.confirm')}
                cancelLabel={t('common.cancel')}
                confirmTone={'danger'}
                onConfirm={() => {
                    if (topicPendingDeletionId) {
                        editor.topics.remove(topicPendingDeletionId);
                    }
                    setTopicPendingDeletionId(null);
                }}
                onCancel={() => setTopicPendingDeletionId(null)}
                dataTestId={'delete-topic-confirm-dialog'}
            />
        </>
    );
};

interface MeetingAgendaBuilderProps {
    cycleInstanceId: string;
    dataTestId?: string;
    disabled?: boolean;
}

export const MeetingAgendaBuilder = ({
    cycleInstanceId,
    dataTestId = 'meeting-agenda-builder',
    disabled = false,
}: MeetingAgendaBuilderProps) => {
    const { t, i18n } = useAmelioTranslations();
    // The agenda is written in one language, its own, whatever the reader's. The reader's language is the one
    // a new agenda (first GET, generation) is written in, and the editor says so when they differ.
    const language: ContentLanguage = toContentLanguage(i18n.language);

    // GET is cycle-keyed. `undefined` while loading or after a failure; `null` once resolved with no
    // agenda yet (→ generate).
    const {
        data: agenda,
        isLoading: isAgendaLoading,
        isError: hasAgendaFailed,
    } = useGetMeetingAgenda(cycleInstanceId);
    const { mutate: updateAgenda } = useUpdateMeetingAgenda(cycleInstanceId);
    const { generatePlanOrReport } = useMeetingAgenda(cycleInstanceId);

    // Only the first auto-generate (on an empty agenda) shows the page skeleton; a manual regenerate shows
    // its progress inside the modal instead.
    const [isAutoGenerating, setIsAutoGenerating] = useState(false);
    const [editorVersion, setEditorVersion] = useState(0);
    const modalRef = useRef<AgendaPromptModalRef>(null);
    const autoGenerated = useRef(false);

    // Auto-generate once when the GET resolves to "no agenda yet". Generation returns the plan to the client
    // (the backend no longer persists it); we save it via the update endpoint — a fresh agenda, so "replace".
    // A manual (re)generate goes through the modal below, where the manager also picks Add vs Replace.
    useEffect(() => {
        if (agenda !== null || autoGenerated.current || isAutoGenerating || disabled) return;
        autoGenerated.current = true;
        void (async () => {
            setIsAutoGenerating(true);
            let generated: GeneratedAgenda | null;
            try {
                generated = await generatePlanOrReport('');
            } catch (error) {
                // Only a noted topic can be refused here: there is no objective yet. Say which one to rewrite.
                if (error instanceof SensitiveObjectiveRefusal) {
                    const topics = error.topics.map(({ title }) =>
                        t('meetingAgenda.aiAgenda.sensitiveTopic.item', { title }),
                    );
                    showErrorToast({
                        message: [
                            t('meetingAgenda.aiAgenda.sensitiveTopic.message'),
                            ...topics,
                        ].join(' '),
                    });
                }
                generated = null;
            }
            setIsAutoGenerating(false);
            if (!generated) {
                return;
            }
            updateAgenda(mergeGeneratedAgenda('replace', generated, null));
        })();
    }, [agenda, isAutoGenerating, generatePlanOrReport, updateAgenda, disabled, t]);

    // The modal's generate step: a failure is reported and returns null, which keeps the modal on the last
    // question step so the manager can retry. The modal asks for the objective only.
    const handleModalGenerate = async (objective: string): Promise<GeneratedAgenda | null> =>
        disabled ? null : generatePlanOrReport(objective);

    // The manager's Add / Replace choice, merged into the agenda as saved: Replace takes the generated
    // plan's language, Add is only offered into an agenda of that language. Once the merge is saved, a
    // fresh editor mounts on it: whatever the editor still held open belonged to the agenda it replaced.
    const handleModalIntegrate = (mode: AgendaIntegrationMode, generated: GeneratedAgenda) => {
        if (disabled) {
            return;
        }

        updateAgenda(mergeGeneratedAgenda(mode, generated, agenda), {
            onSuccess: () => setEditorVersion((version) => version + 1),
        });
    };

    const promptModal = (
        <AgendaPromptModal
            ref={modalRef}
            onGenerate={handleModalGenerate}
            onIntegrate={handleModalIntegrate}
            canAdd={Boolean(agenda?.items.length)}
            agendaLanguage={agenda?.language}
            askContext={false}
        />
    );

    const renderInShell = (children: ReactNode) => (
        <Container
            p={'md'}
            mb={'md'}
            border={'white'}
            borderRadius={'2xl'}
            background={'translucent'}
            dataTestId={dataTestId}
        >
            {children}
        </Container>
    );

    // A GET that failed is not an agenda waiting to be written: offering to generate one would hide the
    // failure behind an invitation, and a generation saved over an agenda the server still holds would
    // replace it. Say so instead, and offer nothing.
    if (hasAgendaFailed) {
        return renderInShell(<Text color={Colors.ERROR_800}>{t('meetingAgenda.loadError')}</Text>);
    }

    // No agenda persisted yet: show a loading state only while the GET or a generation is actually in
    // flight. Once both settle with still no agenda (generation failed, or it "succeeded" but nothing was
    // persisted), fall back to the AI banner so the manager can retry — never an endless skeleton. The
    // objectives modal stays mounted so the retry can collect input.
    if (!agenda) {
        const isBusy = isAgendaLoading || isAutoGenerating;
        return renderInShell(
            <>
                {isBusy ? (
                    <AgendaGeneratingState
                        notice={
                            isAutoGenerating
                                ? {
                                      title: t('meetingAgenda.aiAgenda.overlay.generating'),
                                      message: t('meetingAgenda.aiAgenda.overlay.message'),
                                  }
                                : undefined
                        }
                    />
                ) : (
                    <AgendaAiBanner
                        onGenerate={() => modalRef.current?.open()}
                        isGenerating={isAutoGenerating}
                        disabled={disabled}
                    />
                )}

                {promptModal}
            </>,
        );
    }

    return (
        <Container
            p={'xl'}
            mb={'md'}
            border={'white'}
            borderRadius={'2xl'}
            background={'translucent'}
            dataTestId={dataTestId}
        >
            {/* Remounted only when a generated plan replaced the agenda: its text does not depend on the
                reader's language, so a language switch keeps the editor and what it holds open. */}
            <CycleAgendaEditor
                key={editorVersion}
                agenda={agenda}
                readerLanguage={language}
                disabled={disabled}
                onSave={(payload) => updateAgenda(payload)}
                banner={
                    <AgendaAiBanner
                        onGenerate={() => modalRef.current?.open()}
                        isGenerating={isAutoGenerating}
                        disabled={disabled}
                    />
                }
            />

            {promptModal}
        </Container>
    );
};

export default MeetingAgendaBuilder;
